import type { Coordinates, Location, Store } from 'maps/types'

const HALF_CIRCLE_DEGREES = 180
const DIVIDE_IN_HALF = 2
const TWO_TIMES = 2
const EARTH_RADIUS = 6371 // in kilometers
const ROUNDING = 10
const MAX_DISTANCE = 30 // in kilometers
const MIN_DISTANCE = 0

export function refreshDistance(stores: Store[], location: Location): Store[] {
	const { coordinates } = location
	if (!coordinates?.lat || !coordinates?.lng || !stores.length) {
		return stores
	}

	return stores.map<Store>((store) => {
		const storeCoordinates = store.addresses.coordinates
		const storeDistance = calculateDistance(coordinates, storeCoordinates)
		return {
			...store,
			addresses: {
				...store.addresses,
				coordinates: {
					...storeCoordinates,
					distance: storeDistance,
				},
			},
		}
	})
}

/**
 * Given two coordinates, calculate the distance between them.
 * This uses the ‘haversine’ formula to calculate the great-circle distance between two points.
 * The shortest distance over the earth’s surface – giving an ‘as-the-crow-flies’
 * distance between the points (ignoring any hills they fly over, of course!).
 * @returns distance in kilometers
 */
export function calculateDistance(from: Coordinates, to: Coordinates): number {
	const { lat: lat1, lng: lon1 } = from
	const { lat: lat2, lng: lon2 } = to

	const toRadians = (degrees: number) =>
		degrees * (Math.PI / HALF_CIRCLE_DEGREES)

	const deltaLat = toRadians(lat2 - lat1)
	const deltaLon = toRadians(lon2 - lon1)

	const a =
		Math.sin(deltaLat / DIVIDE_IN_HALF) * Math.sin(deltaLat / DIVIDE_IN_HALF) +
		Math.cos(toRadians(lat1)) *
			Math.cos(toRadians(lat2)) *
			Math.sin(deltaLon / DIVIDE_IN_HALF) *
			Math.sin(deltaLon / DIVIDE_IN_HALF)

	const c = TWO_TIMES * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

	return Math.round(EARTH_RADIUS * c * ROUNDING) / ROUNDING
}

export function filterAndSortByNearbyStore(
	stores: Store[],
	hasDistanceLimit = true
): Store[] {
	return stores
		.filter((store) => {
			const { distance } = store.addresses.coordinates
			return (
				!hasDistanceLimit ||
				(distance >= MIN_DISTANCE && distance <= MAX_DISTANCE)
			)
		})
		.sort(
			(a, b) =>
				a.addresses.coordinates.distance - b.addresses.coordinates.distance
		)
}

export function isSameCoordinates(
	coordinates1?: Coordinates,
	coordinates2?: Coordinates
): boolean {
	if (!coordinates1 || !coordinates2) {
		return coordinates1 === coordinates2
	}

	return (
		coordinates1.lat === coordinates2.lat &&
		coordinates1.lng === coordinates2.lng
	)
}
